import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalAuth } from '../context/AuthContext';

/**
 * The one coloured box the app uses (ClickUp 869fjrytd). Four levels, one
 * colour and icon each, on a quiet surface with a coloured left edge:
 * - action: the viewer has to do something; always has an action, cannot be closed
 * - warning: something is blocked or wrong, but it is not the viewer's turn
 * - info: good to know; can be closed, remembered per user in this browser
 * - done: something is finished; can be closed like info
 * Content is a short title (what happened), one sentence (what it means for
 * me) and at most one action (what I do now) — never the same words twice.
 */
export type NoticeLevel = 'action' | 'warning' | 'info' | 'done';

const ICONS: Record<NoticeLevel, string> = {
  action: 'arrow-right-circle',
  warning: 'alert-triangle',
  info: 'info',
  done: 'check-circle',
};

interface NoticeProps {
  level: NoticeLevel;
  title: React.ReactNode;
  children?: React.ReactNode;
  /** The one thing to do now. Required in practice for level "action". */
  action?: { label: string; onClick: () => void; testId?: string; disabled?: boolean };
  /** What the notice is about, as a small line above the title (e.g. "Kontrakt #1 · Etappmål #2"). */
  context?: React.ReactNode;
  /** Feather icon overriding the level's own (e.g. "clock" while waiting). */
  icon?: string;
  /** Lets an info or done notice be closed, and stay closed, under this key. */
  dismissKey?: string;
  className?: string;
  testId?: string;
}

const storageKey = (userId: string, key: string) => `etappio:notice-dismissed:${userId}:${key}`;

function readDismissed(key: string | null) {
  if (!key) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

export function Notice({ level, title, children, action, context, icon, dismissKey, className = '', testId }: NoticeProps) {
  const { t } = useTranslation();
  // The user only scopes a closed notice; tests that mock the auth module
  // without this hook still render.
  const auth = (useOptionalAuth ?? (() => null))();
  const closable = Boolean(dismissKey) && (level === 'info' || level === 'done');
  const key = closable && dismissKey ? storageKey(auth?.user?.id ?? 'anonymous', dismissKey) : null;
  const [dismissed, setDismissed] = useState(() => readDismissed(key));
  if (dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      if (key) window.localStorage.setItem(key, '1');
    } catch {
      // Private mode: closed for this visit only.
    }
  };

  return (
    <div
      className={`notice is-${level} ${className}`}
      role={level === 'action' || level === 'warning' ? 'alert' : 'status'}
      data-testid={testId}
      data-notice-level={level}
    >
      <i className={`feather-${icon ?? ICONS[level]} notice-icon`} aria-hidden="true"></i>
      <div className="notice-body">
        {context && <div className="notice-context">{context}</div>}
        <div className="notice-title">{title}</div>
        {children && <div className="notice-text">{children}</div>}
        {action && (
          <button type="button" className="btn notice-action" onClick={action.onClick}
            data-testid={action.testId} disabled={action.disabled}>
            {action.label}
          </button>
        )}
      </div>
      {closable && (
        <button type="button" className="notice-close" onClick={dismiss} aria-label={t('noticeBox.close')}>
          <i className="feather-x" aria-hidden="true"></i>
        </button>
      )}
    </div>
  );
}
